import { useState, useEffect, useRef } from "react";
import axios from "../lib/api";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Mail, CheckCircle, AlertCircle, Send, RotateCcw,
  Eye, Edit2, ChevronRight, X, Tag, Palette, Loader2
} from "lucide-react";

const token = () => localStorage.getItem("token");
const headers = () => ({ Authorization: `Bearer ${token()}` });

// ── Template card ──────────────────────────────────────────────────────────────
function TemplateCard({ tmpl, onSelect }) {
  return (
    <button
      data-testid={`template-card-${tmpl.template_id}`}
      onClick={() => onSelect(tmpl)}
      className="w-full text-left bg-white border border-slate-200 rounded-xl p-5 hover:border-slate-400 hover:shadow-sm transition-all group"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
            <Mail className="w-4 h-4 text-slate-600" />
          </div>
          <div>
            <p className="font-semibold text-slate-900 text-sm">{tmpl.name}</p>
            {tmpl.is_customized && (
              <span className="text-xs text-amber-600 font-medium">Personalizado</span>
            )}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-slate-700 transition-colors mt-1" />
      </div>
      <p className="text-xs text-slate-500 mb-3 line-clamp-2">{tmpl.description}</p>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-mono">{tmpl.trigger}</span>
      </div>
      <p className="text-xs text-slate-500 mt-2 truncate italic">{tmpl.subject}</p>
    </button>
  );
}

// ── Color swatch input ─────────────────────────────────────────────────────────
function ColorInput({ label, value, onChange }) {
  return (
    <div className="flex items-center gap-3">
      <input
        type="color"
        value={value || "#000000"}
        onChange={(e) => onChange(e.target.value)}
        className="w-8 h-8 rounded cursor-pointer border border-slate-200 bg-transparent"
        title={label}
      />
      <div className="flex-1">
        <label className="text-xs text-slate-500 block mb-1">{label}</label>
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="h-7 text-xs font-mono"
          placeholder="#000000"
        />
      </div>
    </div>
  );
}

// ── Template Editor Modal ──────────────────────────────────────────────────────
function TemplateEditor({ templateId, onClose, onSaved }) {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [activeTab, setActiveTab] = useState("edit");
  const [previewLoading, setPreviewLoading] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/admin/email/templates/${templateId}`, { headers: headers() })
      .then(r => {
        setConfig(r.data);
        setForm({
          subject: r.data.subject,
          preheader: r.data.preheader,
          heading: r.data.heading,
          body_text: r.data.body_text,
          cta_label: r.data.cta_label,
          logo_text: r.data.logo_text,
          header_bg: r.data.header_bg,
          primary_color: r.data.primary_color,
          footer_text: r.data.footer_text,
        });
      })
      .catch(() => toast.error("Error cargando template"));
  }, [templateId]);

  const loadPreview = () => {
    setPreviewLoading(true);
    axios.get(`${API}/admin/email/templates/${templateId}/preview`, { headers: headers() })
      .then(r => setPreviewHtml(r.data.html))
      .catch(() => toast.error("Error cargando preview"))
      .finally(() => setPreviewLoading(false));
  };

  useEffect(() => {
    if (activeTab === "preview") loadPreview();
  }, [activeTab, templateId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/email/templates/${templateId}`, form, { headers: headers() });
      toast.success("Template guardado correctamente");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando template");
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async () => {
    if (!window.confirm("¿Restaurar template a los valores por defecto?")) return;
    setRestoring(true);
    try {
      const r = await axios.post(`${API}/admin/email/templates/${templateId}/restore`, {}, { headers: headers() });
      const def = r.data.config;
      setForm({
        subject: def.subject, preheader: def.preheader, heading: def.heading,
        body_text: def.body_text, cta_label: def.cta_label, logo_text: def.logo_text,
        header_bg: def.header_bg, primary_color: def.primary_color, footer_text: def.footer_text,
      });
      toast.success("Template restaurado a valores por defecto");
      onSaved?.();
    } catch (e) {
      toast.error("Error restaurando template");
    } finally {
      setRestoring(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) return toast.error("Introduce un email de prueba");
    setSending(true);
    try {
      await axios.post(`${API}/admin/email/templates/${templateId}/test`, { email: testEmail }, { headers: headers() });
      toast.success(`Email de prueba enviado a ${testEmail}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error enviando email de prueba");
    } finally {
      setSending(false);
    }
  };

  if (!config) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  const tabs = [
    { id: "edit", label: "Editar", icon: Edit2 },
    { id: "preview", label: "Preview", icon: Eye },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 flex items-start justify-end z-50" onClick={onClose}>
      <div
        className="h-full w-full max-w-4xl bg-white shadow-2xl flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid={`template-editor-${templateId}`}
      >
        {/* Drawer Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div>
            <h2 className="font-semibold text-slate-900 text-base">{config.name}</h2>
            <p className="text-xs text-slate-500 mt-0.5 font-mono">{config.trigger}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 px-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-slate-900 text-slate-900"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === "edit" && (
            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: Content fields */}
              <div className="space-y-5">
                <div>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Contenido del email</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs text-slate-600 font-medium block mb-1">Subject *</label>
                      <Input
                        data-testid="field-subject"
                        value={form.subject || ""}
                        onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                        placeholder="Subject line..."
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-600 font-medium block mb-1">Preheader</label>
                      <Input
                        data-testid="field-preheader"
                        value={form.preheader || ""}
                        onChange={e => setForm(f => ({ ...f, preheader: e.target.value }))}
                        placeholder="Preview text (invisible in email body)..."
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-600 font-medium block mb-1">Título principal</label>
                      <Input
                        data-testid="field-heading"
                        value={form.heading || ""}
                        onChange={e => setForm(f => ({ ...f, heading: e.target.value }))}
                        placeholder="Heading..."
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-600 font-medium block mb-1">Texto principal</label>
                      <textarea
                        data-testid="field-body-text"
                        value={form.body_text || ""}
                        onChange={e => setForm(f => ({ ...f, body_text: e.target.value }))}
                        rows={4}
                        className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-slate-400 resize-none"
                        placeholder="Body text..."
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-slate-600 font-medium block mb-1">CTA Label</label>
                        <Input
                          data-testid="field-cta-label"
                          value={form.cta_label || ""}
                          onChange={e => setForm(f => ({ ...f, cta_label: e.target.value }))}
                          placeholder="Button text..."
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-600 font-medium block mb-1">Logo / Brand name</label>
                        <Input
                          data-testid="field-logo-text"
                          value={form.logo_text || ""}
                          onChange={e => setForm(f => ({ ...f, logo_text: e.target.value }))}
                          placeholder="Ecommsystem"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-slate-600 font-medium block mb-1">Footer</label>
                      <Input
                        data-testid="field-footer-text"
                        value={form.footer_text || ""}
                        onChange={e => setForm(f => ({ ...f, footer_text: e.target.value }))}
                        placeholder="Footer tagline..."
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Colors + Variables */}
              <div className="space-y-5">
                <div>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <Palette className="w-3.5 h-3.5" /> Colores
                  </h3>
                  <div className="space-y-4 bg-slate-50 rounded-xl p-4">
                    <ColorInput
                      label="Header background"
                      value={form.header_bg}
                      onChange={v => setForm(f => ({ ...f, header_bg: v }))}
                    />
                    <ColorInput
                      label="Color principal (botón CTA)"
                      value={form.primary_color}
                      onChange={v => setForm(f => ({ ...f, primary_color: v }))}
                    />
                  </div>
                </div>

                {/* Variables */}
                <div>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <Tag className="w-3.5 h-3.5" /> Variables disponibles
                  </h3>
                  <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                    <p className="text-xs text-slate-500">Usa <code className="bg-slate-200 px-1 rounded">{'{variable}'}</code> en subject, heading o body_text:</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {(config.variables || []).map(v => (
                        <span key={v} className="text-xs font-mono bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded">
                          {`{${v}}`}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Send test */}
                <div>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <Send className="w-3.5 h-3.5" /> Enviar email de prueba
                  </h3>
                  <div className="flex gap-2">
                    <Input
                      data-testid="test-email-input"
                      type="email"
                      value={testEmail}
                      onChange={e => setTestEmail(e.target.value)}
                      placeholder="email@ejemplo.com"
                      className="flex-1 text-sm"
                    />
                    <Button
                      data-testid="btn-send-test"
                      onClick={handleSendTest}
                      disabled={sending || !testEmail}
                      size="sm"
                      className="shrink-0"
                    >
                      {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "preview" && (
            <div className="h-full p-4">
              {previewLoading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : (
                <iframe
                  ref={iframeRef}
                  data-testid="email-preview-iframe"
                  srcDoc={previewHtml}
                  className="w-full rounded-lg border border-slate-200"
                  style={{ height: "calc(100vh - 200px)" }}
                  title="Email Preview"
                />
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-3">
          <Button
            data-testid="btn-restore-default"
            variant="outline"
            size="sm"
            onClick={handleRestore}
            disabled={restoring}
            className="text-slate-600"
          >
            {restoring ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1.5" />}
            Restaurar por defecto
          </Button>
          <div className="flex gap-3">
            <Button data-testid="btn-cancel-editor" variant="ghost" size="sm" onClick={onClose}>Cancelar</Button>
            <Button
              data-testid="btn-save-template"
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="bg-slate-900 text-white hover:bg-slate-800"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
              Guardar cambios
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function AdminEmailPage() {
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);

  const loadData = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/email/settings`, { headers: headers() }),
      axios.get(`${API}/admin/email/templates`, { headers: headers() }),
    ])
      .then(([sRes, tRes]) => {
        setSettings(sRes.data);
        setTemplates(tRes.data.templates);
      })
      .catch(() => toast.error("Error cargando datos de email"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const handleSendSettingsTest = async () => {
    if (!testEmail) return toast.error("Introduce un email de prueba");
    setSending(true);
    try {
      await axios.post(`${API}/admin/email/templates/welcome/test`, { email: testEmail }, { headers: headers() });
      toast.success(`Email de prueba enviado a ${testEmail}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error enviando email de prueba");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-email-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Email</h1>
        <p className="text-sm text-slate-500 mt-1">Gestión del sistema de emails transaccionales.</p>
      </div>

      {/* Settings Card */}
      <Card data-testid="email-settings-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="w-4 h-4 text-slate-600" />
            Configuración del proveedor
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1">Proveedor</p>
              <p className="font-semibold text-slate-900 text-sm">{settings?.provider || "—"}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1">Remitente</p>
              <p className="font-semibold text-slate-900 text-xs font-mono truncate">{settings?.sender || "—"}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1">Dominio</p>
              <p className="font-semibold text-slate-900 text-sm">{settings?.domain || "—"}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1">Estado</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                {settings?.configured ? (
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-red-500" />
                )}
                <span className={`text-sm font-medium ${settings?.configured ? "text-emerald-600" : "text-red-600"}`}>
                  {settings?.configured ? "Activo" : "No configurado"}
                </span>
              </div>
            </div>
          </div>

          {settings?.configured && (
            <div className="flex items-center gap-2 pt-4 border-t border-slate-100">
              <p className="text-xs text-slate-500 shrink-0">API Key:</p>
              <code className="text-xs bg-slate-100 px-2 py-0.5 rounded text-slate-600 font-mono">{settings.api_key_hint}</code>
              <span className="text-xs text-slate-400 ml-1">(gestionada via variable de entorno)</span>
            </div>
          )}

          <div className="flex items-center gap-3 mt-4">
            <Input
              data-testid="settings-test-email-input"
              type="email"
              value={testEmail}
              onChange={e => setTestEmail(e.target.value)}
              placeholder="Enviar email de prueba a..."
              className="max-w-xs text-sm"
            />
            <Button
              data-testid="btn-settings-send-test"
              onClick={handleSendSettingsTest}
              disabled={sending || !testEmail || !settings?.configured}
              size="sm"
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
              Enviar prueba
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Templates */}
      <div>
        <h2 className="text-base font-semibold text-slate-900 mb-4">Templates de email</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-grid">
          {templates.map(tmpl => (
            <TemplateCard
              key={tmpl.template_id}
              tmpl={tmpl}
              onSelect={() => setSelectedTemplate(tmpl.template_id)}
            />
          ))}
        </div>
      </div>

      {/* Template Editor Drawer */}
      {selectedTemplate && (
        <TemplateEditor
          templateId={selectedTemplate}
          onClose={() => setSelectedTemplate(null)}
          onSaved={() => { loadData(); }}
        />
      )}
    </div>
  );
}
